import React, { useState, useRef } from 'react';
import Box from '@amzn/awsui-components-console/box';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Badge from '@amzn/awsui-components-console/badge';
import Avatar from '@amzn/awsui-chat-components-console/avatar';
import ReactMarkdown from 'react-markdown';

import FeedbackDialog from '../pages/chat/additional-info/feedback-dialog';
import { FeedbackActions } from '../pages/chat/common-components';
import { FeedbackData, FeedbackResponse } from '../services/api';

interface SummaryEventBubbleProps {
  problemStatement?: string;
  problemStatementExt?: string;
  tools_used?: string[];
  relevantDetails?: string | string[];
  customer_facing_suggested_response?: string;
  onFeedbackSubmit?: (data: FeedbackData) => Promise<FeedbackResponse>;
  sessionId?: string | null;
}

export const SummaryEventBubble: React.FC<SummaryEventBubbleProps> = ({
  problemStatement,
  problemStatementExt,
  tools_used,
  relevantDetails,
  customer_facing_suggested_response,
  onFeedbackSubmit,
  sessionId,
}) => {
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [isPositiveFeedback, setIsPositiveFeedback] = useState(false);
  const messageIdRef = useRef(`summary-${Date.now()}`);

  const createSummaryResponseObject = () => ({
    problemStatement,
    problemStatementExt,
    tools_used,
    relevantDetails,
    customer_facing_suggested_response
  });

  const renderFeedbackActions = () => {
    if (!onFeedbackSubmit || !sessionId) return null;

    return (
      <FeedbackActions
        contentToCopy={JSON.stringify(createSummaryResponseObject())}
        onFeedbackClick={(isPositive) => {
          setIsPositiveFeedback(isPositive);
          setShowFeedbackDialog(true);
        }}
        userMessage="Summary Event"
        aiResponse={JSON.stringify(createSummaryResponseObject())}
        sessionId={sessionId}
        messageId={messageIdRef.current}
        hideCopy={true}
      />
    );
  };

  return (
    <div style={{
      backgroundColor: '#f8f9fa',
      border: '1px solid #e1e4e8',
      borderRadius: '12px',
      padding: '16px',
      margin: '8px 0',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      position: 'relative',
      width: '100%'
    }}>
      <div style={{
        position: 'absolute',
        right: '-8px',
        top: '16px',
        width: '0',
        height: '0',
        borderTop: '8px solid transparent',
        borderBottom: '8px solid transparent',
        borderLeft: '8px solid #f8f9fa'
      }} />
      
      <SpaceBetween size="s">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Avatar color="gen-ai" iconName="gen-ai" ariaLabel="AI Assistant" />
            <Badge color="blue">SUMMARY</Badge>
            <Badge color="grey">Customer ↔ Pharmacy Assistant</Badge>
          </div>
          {renderFeedbackActions()}
        </div>
        
        <Box>
          {problemStatement && (
            <Box margin={{ bottom: 'xs' }}>
              <ReactMarkdown>{problemStatement}</ReactMarkdown>
            </Box>
          )}
          {problemStatementExt && (
            <div style={{ fontSize: '14px', lineHeight: '1.5' }}>
              <ReactMarkdown>{problemStatementExt}</ReactMarkdown>
            </div>
          )}
        </Box>

      </SpaceBetween>

      {showFeedbackDialog && onFeedbackSubmit && sessionId && (
        <FeedbackDialog
          onDismiss={() => setShowFeedbackDialog(false)}
          onSubmit={async (data) => {
            try {
              await onFeedbackSubmit(data);
              setShowFeedbackDialog(false);
            } catch (error) {
              console.error('Failed to submit feedback:', error);
              // Keep dialog open on error so user can retry
            }
          }}
          userMessage="Summary Event"
          aiResponse={JSON.stringify(createSummaryResponseObject())}
          sessionId={sessionId}
          isPositive={isPositiveFeedback}
        />
      )}
    </div>
  );
};